import { useEffect, useRef, useState } from 'react';
import {
  USER_PATH_REFETCH_M, OFF_PATH_M, nearestOnPath, trimPathToUser,
  calcDistance, WALKABLE_MAX_M, fetchOsrmRoute,
} from '../utils/walk';

// Насколько дальше самого близкого прохода может быть «первый» проход
// мимо места, по которому режется маршрут
const CUT_TOLERANCE_M = 25;

const meters = (a, b) => {
  const dy = (a[0] - b[0]) * 110540;
  const dx = (a[1] - b[1]) * 111320 * Math.cos((a[0] * Math.PI) / 180);
  return Math.hypot(dx, dy);
};

const pathLength = (path) => path.reduce((sum, pt, i) => (
  i ? sum + calcDistance(path[i - 1][0], path[i - 1][1], pt[0], pt[1]) : 0
), 0);

/**
 * Линии карты прогулки (брендбук 2.11): маршрут целиком, путь «сейчас»
 * до цели и их разрезка на «позади / к цели / впереди».
 *
 * - `points` — координаты мест по порядку, `null` у места без координат;
 * - `currentIndex` — к какому месту идём;
 * - `userPosition` — `[lat, lng]` или null;
 * - `stationPoint` — станция метро, от которой вести путь к первому
 *   месту, пока нет геолокации.
 *
 * Пеший путь строит наш бэкенд: координаты не уходят наружу с IP
 * пользователя (CLAUDE.md, «Что уходит наружу»).
 */
const useWalkPaths = ({ points, currentIndex, userPosition, stationPoint }) => {
  const [osrmPath, setOsrmPath] = useState(null);
  // Пеший путь не построился — тогда и только тогда рисуем по прямой.
  // Пока ответ в пути, линии нет: прямая сквозь дома, которую через
  // секунду подменяет настоящий путь, читалась как ошибка
  const [osrmFailed, setOsrmFailed] = useState(false);
  const [osrmUserPath, setOsrmUserPath] = useState(null);
  const [osrmUserFailed, setOsrmUserFailed] = useState(false);
  // Пеший путь от станции метро до первого места — пока нет геолокации.
  // null — ещё не построен, 'failed' — не построился
  const [metroPath, setMetroPath] = useState(null);

  // Маршрут целиком. Перестраивается, только когда меняется сам маршрут:
  // ключ — координаты по порядку (свой маршрут можно переставить)
  const pointsKey = JSON.stringify(points);
  useEffect(() => {
    const pts = points.filter(Boolean);
    setOsrmPath(null);
    setOsrmFailed(false);
    if (pts.length < 2) return undefined;
    let cancelled = false;
    fetchOsrmRoute(pts).then((path) => {
      if (cancelled) return;
      if (path) setOsrmPath(path); else setOsrmFailed(true);
    });
    return () => { cancelled = true; };
  }, [pointsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Без геолокации путь к первому месту начинается от метро: так человек
  // видит, куда идти, ещё не включив гео. Координаты пользователя тут
  // не участвуют — только станция и место
  const firstPoint = points[0];
  const metroKey = stationPoint && firstPoint ? JSON.stringify([stationPoint, firstPoint]) : '';
  useEffect(() => {
    setMetroPath(null);
    if (!metroKey) return undefined;
    let cancelled = false;
    fetchOsrmRoute([stationPoint, firstPoint]).then((path) => {
      if (!cancelled) setMetroPath(path && path.length > 1 ? path : 'failed');
    });
    return () => { cancelled = true; };
  }, [metroKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Пешеходный маршрут от геопозиции пользователя до текущей точки
  const osrmUserFetchingRef = useRef(false);
  const lastUserTargetRef = useRef(null);
  // Откуда строился последний путь до цели
  const lastUserOriginRef = useRef(null);
  const target = points[currentIndex] || null;
  const targetKey = target ? `${currentIndex}|${target[0]}|${target[1]}` : null;
  useEffect(() => {
    if (!userPosition || !target) return;

    // Дальше пешей доступности путь не строим: до Выборга из города
    // сервис честно рисовал 114 км пешком — тяжёлый запрос и ломаная
    // через всю карту. Человек туда едет, а не идёт
    if (calcDistance(userPosition[0], userPosition[1], target[0], target[1]) > WALKABLE_MAX_M) {
      lastUserTargetRef.current = null;
      lastUserOriginRef.current = null;
      setOsrmUserPath(null);
      setOsrmUserFailed(false);
      return;
    }

    // При смене цели — сбросить старый путь
    if (lastUserTargetRef.current !== targetKey) {
      lastUserTargetRef.current = targetKey;
      lastUserOriginRef.current = null;
      setOsrmUserPath(null);
      setOsrmUserFailed(false);
    }

    // Пересчитываем, только если человек заметно сдвинулся. Позиция
    // приходит раз в 5 секунд, и раньше каждая уходила запросом в прокси
    // маршрутов — постоянная нагрузка, а при ограничении частоты линия
    // срывалась в «по прямой». Мелкие сдвиги — это дрожание GPS.
    // Исключение — человек сошёл с линии: тогда путь строится сразу
    const origin = lastUserOriginRef.current;
    const offPath = osrmUserPath && osrmUserPath.length > 1
      && nearestOnPath(userPosition, osrmUserPath).dist > OFF_PATH_M;
    if (origin && !offPath
      && calcDistance(origin[0], origin[1], userPosition[0], userPosition[1]) < USER_PATH_REFETCH_M) return;

    // Не запускать параллельные запросы
    if (osrmUserFetchingRef.current) return;
    osrmUserFetchingRef.current = true;
    lastUserOriginRef.current = userPosition;

    const requestedFor = targetKey;
    fetchOsrmRoute([userPosition, target]).then((path) => {
      osrmUserFetchingRef.current = false;
      // Пока ждали ответа, цель сменилась — путь к старой не рисуем,
      // а следующее обновление позиции построит путь к новой
      if (lastUserTargetRef.current !== requestedFor) {
        lastUserOriginRef.current = null;
        return;
      }
      if (path) { setOsrmUserPath(path); setOsrmUserFailed(false); } else setOsrmUserFailed(true);
    });
  }, [userPosition, targetKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Разрезка маршрута ──
  // Маршрут режется по местам на три части: позади (серая), к текущей
  // цели и впереди. Без пешего пути — отрезки по прямой между местами,
  // но только когда путь точно не построился
  const routeLine = osrmPath && osrmPath.length > 1
    ? osrmPath
    : (osrmFailed ? points.filter(Boolean) : null);
  // Берётся ПЕРВЫЙ проход мимо места, а не самый близкий: пеший путь
  // бывает петлёй и возвращается к месту ещё раз. Тогда разрез вставал
  // на дальний проход, и часть пути к следующему месту попадала
  // в «пройденное». Первое место — всегда начало линии
  const cutAt = [];
  if (routeLine) {
    let from = 0;
    points.forEach((pt, i) => {
      if (!pt || i === 0) { cutAt[i] = from; return; }
      let minD = Infinity;
      for (let k = from; k < routeLine.length; k++) minD = Math.min(minD, meters(routeLine[k], pt));
      let cut = routeLine.length - 1;
      for (let k = from; k < routeLine.length; k++) {
        if (meters(routeLine[k], pt) <= minD + CUT_TOLERANCE_M) { cut = k; break; }
      }
      cutAt[i] = cut;
      from = cut;
    });
  }
  const sliceLine = (a, b) => (routeLine && b > a ? routeLine.slice(a, b + 1) : null);
  const finished = currentIndex >= points.length;
  const prevCut = currentIndex > 0 ? cutAt[Math.min(currentIndex, points.length) - 1] : 0;
  const passedLine = currentIndex > 0 ? sliceLine(0, finished ? routeLine?.length - 1 : prevCut) : null;
  const legLine = finished ? null : sliceLine(prevCut, cutAt[currentIndex]);
  const aheadLine = routeLine && !finished ? sliceLine(cutAt[currentIndex], routeLine.length - 1) : null;

  // От метро до первого места — по пешему пути без крюка у станции:
  // по прямой через парк выходило вдвое меньше, а крюк удваивал
  const stationPath = stationPoint && Array.isArray(metroPath)
    ? (trimPathToUser(stationPoint, metroPath) || metroPath)
    : null;
  const stationDistance = stationPoint && firstPoint
    ? (stationPath ? pathLength(stationPath) : calcDistance(stationPoint[0], stationPoint[1], firstPoint[0], firstPoint[1]))
    : null;

  // Путь «сейчас» до цели: от человека, а без геолокации на старте —
  // от метро. Пеший; по прямой — только если пеший не построился.
  // Пройденный кусок отрезается: линия всегда начинается от человека,
  // хотя сам путь перестраивается раз в USER_PATH_REFETCH_M
  let guideLine = null;
  let guideIsRoute = false;
  if (userPosition && target) {
    if (osrmUserPath && osrmUserPath.length > 1) {
      // Сошёл с пути — рисуем как есть: новый путь уже запрошен
      guideLine = trimPathToUser(userPosition, osrmUserPath) || osrmUserPath;
      guideIsRoute = true;
    } else if (osrmUserFailed) {
      guideLine = [userPosition, target];
    }
  } else if (stationPoint && target && currentIndex === 0) {
    if (stationPath) {
      guideLine = stationPath;
      guideIsRoute = true;
    } else if (metroPath === 'failed') {
      guideLine = [stationPoint, target];
    }
  }

  return { passedLine, legLine, aheadLine, guideLine, guideIsRoute, stationDistance, routeLine };
};

export default useWalkPaths;
